import { Box, styled, Typography } from '@mui/material';
import color from 'src/theme/color';

const mdxEditorBaseStyles = {
    '& .mdxeditor': {
        '--accentBase': color.lightningYellow,
        '--accentBgSubtle': 'rgba(250, 187, 24, 0.08)',
        '--accentLine': color.lightningYellow,
        '--accentSolid': color.lightningYellow,
        '--accentText': color.lightningYellow,
        '--accentTextContrast': '#000',
        '--basePageBg': color.darkBlue,
        '--baseBg': color.darkBlue,
        '--baseBgSubtle': color.blackRock,
        '--baseBgActive': '#353747',
        '--baseBase': '#26262B',
        '--baseBorder': '#26262B',
        '--baseLine': '#26262B',
        '--baseSolidHover': '#353747',
        '--baseText': color.white,
        '--baseTextHighContrast': color.white,
        '--baseTextContrast': color.white90,
        '--baseTextMuted': color.white50,
        fontFamily: '"Inter", sans-serif',
        borderRadius: '6px'
    }
};

// Accepts an optional `contentHeight` prop to set a custom min-height on the
// editable area without needing an inline sx override at the call site.
export const MDXEditorWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'contentHeight'
})<{ contentHeight?: number }>(({ contentHeight }) => ({
    border: `1px solid ${color.lightningYellow}`,
    borderRadius: '6px',
    isolation: 'isolate',
    position: 'relative',

    ...mdxEditorBaseStyles,

    ...(contentHeight && {
        '& .mdx-content-editable': {
            minHeight: `${contentHeight}px`
        }
    }),

    '& .mdxeditor-toolbar': {
        backgroundColor: color.darkBlue,
        borderBottom: '1px solid #26262B',
        borderRadius: '6px 6px 0 0',
        flexWrap: 'wrap',
        gap: '2px'
    },

    '& .mdx-content-editable': {
        minHeight: '250px',
        maxHeight: '400px',
        overflowY: 'auto',
        padding: '12px 16px',
        color: color.white,
        fontSize: '14px',
        lineHeight: '1.6',
        backgroundColor: color.darkBlue,
        caretColor: `${color.white} !important`,
        borderRadius: '0 0 6px 6px'
    },

    '& .mdx-content-editable p': {
        minHeight: '1.6em'
    },

    '& .mdxeditor-popup-container': {
        zIndex: 9999
    },

    '& [class*="popoverContent"], & [class*="dialogContent"]': {
        backgroundColor: `${color.darkBlue} !important`,
        borderColor: `#26262B !important`,
        color: `${color.white} !important`
    },

    '& [class*="popoverContent"] label, & [class*="dialogContent"] label': {
        color: `${color.white90} !important`
    },

    '& [class*="popoverContent"] input, & [class*="popoverContent"] [class*="textInput"], & [class*="popoverContent"] [class*="downshiftAutoCompleteSuggestions"]':
        {
            backgroundColor: `#26262B !important`,
            color: `${color.white} !important`,
            borderColor: `#353747 !important`
        },

    '& [class*="primaryButton"]': {
        backgroundColor: `${color.lightningYellow} !important`,
        color: '#000 !important'
    },

    '& [class*="secondaryButton"]': {
        backgroundColor: `${color.blackRock} !important`,
        color: `${color.white} !important`,
        borderColor: `#353747 !important`
    },

    '& [class*="selectTrigger"], & [class*="toolbarButtonActive"]': {
        backgroundColor: `${color.blackRock} !important`
    },

    '& .mdx-attachment-btn': {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        padding: '4px',
        cursor: 'pointer',
        borderRadius: '4px',
        '&:hover': {
            backgroundColor: 'rgba(250, 187, 24, 0.1)'
        }
    }
}));

export const CodeBlockWrapper = styled(Box)({
    backgroundColor: '#1a1c25',
    border: '1px solid #26262B',
    borderRadius: '6px',
    overflowX: 'auto',
    margin: '8px 0',
    '& pre': {
        border: 'none',
        borderRadius: 0,
        margin: 0
    },
    '& code': {
        border: 'none',
        borderRadius: 0,
        padding: 0,
        backgroundColor: 'transparent'
    }
});

export const CodeBlockLanguageTag = styled(Typography)({
    display: 'block',
    fontSize: '11px',
    color: '#6b7280',
    padding: '8px 16px 0',
    fontFamily: 'Inter, sans-serif'
});

export const MDXViewerWrapper = styled(Box)({
    ...mdxEditorBaseStyles,

    '& .mdxeditor-toolbar': {
        display: 'none'
    },

    '& .mdx-viewer-content': {
        padding: '0',
        color: `${color.white} !important`,
        fontSize: '14px',
        lineHeight: '1.6',
        backgroundColor: 'transparent'
    },

    '& .mdxeditor': {
        ...mdxEditorBaseStyles['& .mdxeditor'],
        borderRadius: 0,
        border: 'none'
    },

    '& .mdx-viewer-content pre, & .mdx-viewer-content [data-lexical-decorator] pre':
        {
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: 0,
            padding: 0,
            overflowX: 'auto',
            margin: 0
        },

    '& .mdx-viewer-content pre code, & .mdx-viewer-content [data-lexical-decorator] code':
        {
            backgroundColor: 'transparent',
            color: '#e2e8f0',
            fontFamily: '"Fira Code", "Fira Mono", "Roboto Mono", monospace',
            fontSize: '13px',
            lineHeight: '1.6',
            padding: 0,
            border: 'none',
            borderRadius: 0
        },

    '& .mdx-viewer-content code': {
        backgroundColor: '#1a1c25',
        color: '#e2e8f0',
        fontFamily: '"Fira Code", "Fira Mono", "Roboto Mono", monospace',
        fontSize: '13px',
        borderRadius: '4px',
        padding: '2px 5px',
        border: '1px solid #26262B'
    },

    '& .mdx-viewer-content [class*="codeMirrorWrapper"], & .mdx-viewer-content [class*="codeblock"]':
        {
            backgroundColor: '#1a1c25',
            borderRadius: '6px',
            border: '1px solid #26262B',
            margin: '8px 0',
            padding: '12px 16px',
            color: '#e2e8f0',
            fontFamily: '"Fira Code", "Fira Mono", "Roboto Mono", monospace',
            fontSize: '13px',
            overflowX: 'auto'
        }
});

// ─── Mermaid block ────────────────────────────────────────────────────────────

export const MermaidErrorBox = styled('div')`
    padding: 12px;
    background-color: #2a1215;
    border: 1px solid #5c2b2e;
    border-radius: 4px;
    color: #f87171;
    font-size: 13px;
    font-family: monospace;
`;

export const MermaidDiagramContainer = styled('div')`
    padding: 16px;
    display: flex;
    justify-content: center;
    background-color: #16181f;
    border-radius: 4px;
    overflow: auto;
`;

export const MermaidClickable = styled('div')`
    cursor: pointer;
`;

export const MermaidEditWrapper = styled('div')`
    position: relative;
`;

export const MermaidEditTextarea = styled('textarea')`
    width: 100%;
    min-height: 120px;
    background-color: #1e202a;
    color: #ffffff;
    border: 1px solid #fabb18;
    border-radius: 4px;
    padding: 12px;
    font-family: monospace;
    font-size: 13px;
    resize: vertical;
    outline: none;
    box-sizing: border-box;
`;

// ─── Code block ───────────────────────────────────────────────────────────────

export const CodeTextarea = styled('textarea')`
    width: 100%;
    background-color: #292b36;
    color: #ffffff;
    border: 1px solid #26262b;
    border-radius: 4px;
    padding: 12px;
    font-family: monospace;
    font-size: 13px;
    resize: none;
    outline: none;
    overflow: hidden;
    box-sizing: border-box;
`;
